import { Autocomplete, Box, CircularProgress, TextField } from "@mui/material";
import { UUID } from "adaptivecards";
import { sortBy } from "lodash-es";
import { type FC, useCallback, useEffect, useMemo } from "react";
import type { FieldError } from "react-hook-form";
import type { UseSupabaseLoaderState } from "../../../../../server/supabase/hooks/types";
import { useAccountId } from "../../../../generic/hooks/use-account-id";
import type { MomentFormSurvey } from "../../../../survey/server/supabase-survey-service";
import { makeSurveyUrl } from "../utils/survey-url";

export const SurveySelectorDropdown: FC<{
  surveyLoader: UseSupabaseLoaderState<MomentFormSurvey[]>;
  error?: FieldError;
  onChange: (value: string) => void;
  id?: UUID;
  channel: boolean;
}> = ({ onChange, surveyLoader, error, id, channel }) => {
  const { data: surveys, loading: surveysLoading } = surveyLoader;

  const acc_id = useAccountId();

  const handleChange = useCallback(
    (_: React.SyntheticEvent, newValue: MomentFormSurvey | null) => {
      if (newValue) {
        onChange(makeSurveyUrl(acc_id ?? "", newValue.id, { anonymous: channel }));
      } else {
        onChange("");
      }
    },
    [onChange, acc_id, channel],
  );

  // if Only 1 survey, auto select it
  const defaultValue = useMemo(() => {
    if (!surveys || !surveys.length) return null;
    if (id) return surveys.find((s) => s.id === id) ?? null;

    const defaultSurvey = surveys?.length === 1 ? surveys?.[0] : null;

    return defaultSurvey ?? null;
  }, [id, surveys]);

  useEffect(() => {
    if (defaultValue)
      onChange(makeSurveyUrl(acc_id ?? "", defaultValue.id, { anonymous: channel }));
  }, [defaultValue, onChange, acc_id, channel]);

  if (surveysLoading || !surveys || !surveys.length) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {surveys?.length === 0 ? <>No surveys available</> : <CircularProgress />}
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Autocomplete
        options={sortBy(surveys, "title").filter(
          (survey) => !channel || survey.enable_generic_links,
        )}
        getOptionLabel={(option) => option.title}
        getOptionKey={(option) => option.id}
        defaultValue={defaultValue}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Survey"
            variant="standard"
            error={Boolean(error)}
            helperText={error?.message}
          />
        )}
        onChange={handleChange}
      />
    </Box>
  );
};
